import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Crown as _CrownUnused, Sparkles, CreditCard, Check, Zap, Shield, Scale, Gavel, BellRing, BookOpen, HandHeart, Building2, Trash2, BarChart3, MessageSquare, Landmark, GraduationCap } from "lucide-react";

const PLAN_ICONS = { standard: Zap, plus: Shield, professional: Scale, elite: Sparkles };
const LOOKUP_KEY = {
  standard:     { monthly: "communityiq_standard_monthly",     yearly: "communityiq_standard_yearly"     },
  plus:         { monthly: "communityiq_plus_monthly",         yearly: "communityiq_plus_yearly"         },
  professional: { monthly: "communityiq_professional_monthly", yearly: "communityiq_professional_yearly" },
  elite:        { monthly: "communityiq_elite_monthly",        yearly: "communityiq_elite_yearly"        },
};
const ADDON_ICONS = {
  legal_templates: Gavel, escalation: Scale, reminders: BellRing,
  ai_saint: Sparkles, state_laws: BookOpen, amendment_advisor: HandHeart,
  financial_reports: BarChart3, community_forum: MessageSquare,
  elections_kit: Landmark, training_kits: GraduationCap,
};

function RoiStat({ label, value, hint }) {
  return (
    <div className="text-center md:text-right">
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="text-lg md:text-xl font-mono font-semibold text-gold">
        {value}{hint && <span className="text-xs text-muted-foreground ml-0.5">{hint}</span>}
      </div>
    </div>
  );
}

export default function Subscription() {
  const { user } = useAuth();
  const isBoard = user?.role === "admin" || user?.role === "board";
  const [meta, setMeta] = useState({ plans: [], addons: [] });
  const [sub, setSub] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [npOpen, setNpOpen] = useState(false);
  const [npForm, setNpForm] = useState({ ein: "", org_name: "" });
  const [impact, setImpact] = useState(null);

  const load = async () => {
    try {
      const [m, s, i] = await Promise.all([
        api.get("/plans"),
        api.get("/subscription"),
        api.get("/impact-stats"),
      ]);
      setMeta(m.data);
      setSub(s.data);
      setCycle(s.data.billing_cycle);
      setImpact(i.data);
    } catch (err) {
      console.error("Subscription load failed:", err);
      toast.error(err.response?.data?.detail || "Could not load subscription");
    }
  };

  useEffect(() => { load(); }, []);

  const changePlan = async (plan_id, billing_cycle = cycle) => {
    try {
      const { data } = await api.post("/subscription", { plan_id, billing_cycle });
      setSub(data); setCycle(data.billing_cycle);
      toast.success(`Now on ${plan_id.toUpperCase()} · ${billing_cycle}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not change plan"); }
  };

  const startTrial = async (aid) => {
    try {
      const { data } = await api.post(`/subscription/addons/${aid}/trial`);
      setSub(data);
      toast.success("14-day free trial started");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not start trial"); }
  };

  const activate = async (aid) => {
    try {
      const { data } = await api.post(`/subscription/addons/${aid}`);
      setSub(data);
      toast.success("Add-on activated");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not activate"); }
  };

  const remove = async (aid) => {
    try {
      const { data } = await api.delete(`/subscription/addons/${aid}`);
      setSub(data);
      toast.success("Add-on removed");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not remove"); }
  };

  const claimNonprofit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/subscription/nonprofit-discount", npForm);
      setSub(data);
      setNpOpen(false);
      toast.success("Nonprofit discount applied — 20% off yearly");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not apply"); }
  };

  const [checkingOut, setCheckingOut] = useState(null); // plan_id
  const checkout = async (plan_id) => {
    const lk = LOOKUP_KEY[plan_id]?.[cycle];
    if (!lk) return;
    setCheckingOut(plan_id);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lk,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error("Could not start checkout");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally {
      setCheckingOut(null);
    }
  };

  const price = (p) => (cycle === "yearly" ? p.yearly : p.monthly);
  const suffix = cycle === "yearly" ? "/yr" : "/mo";
  const addonStatus = (aid) => sub?.active_addons?.find((a) => a.addon_id === aid);
  const isIncluded = (aid) => sub?.included_addon_ids?.includes(aid);

  if (!sub) return <div className="text-muted-foreground">Loading subscription…</div>;

  return (
    <div className="space-y-8" data-testid="subscription-page">
      <div>
        <div className="flex items-center gap-2 text-gold text-sm font-medium mb-2">
          <CreditCard className="w-4 h-4" /> Subscription & Billing
        </div>
        <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight">Manage your HOA plan</h1>
        <p className="no-gold text-foreground/75 mt-2">
          Try any add-on for 14 days free. Cancel anytime. Nonprofit? Save 20% on yearly.
        </p>
      </div>

      {/* Work-order ROI banner — surfaces Pro/Elite value at the moment of decision */}
      <div className="hoa-card royal-ring p-6 md:p-7 relative overflow-hidden" data-testid="roi-banner">
        <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-primary/25 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col md:flex-row md:items-center gap-5">
          <div className="w-12 h-12 rounded-xl bg-gold text-secondary-foreground flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-gold-soft mb-1">Pay-yourself-back math</div>
            <h3 className="text-xl md:text-2xl font-semibold leading-tight">
              <span className="text-gold">Elite</span> pays for itself on <span className="text-gold">one work order.</span>
            </h3>
            <p className="no-gold text-sm text-foreground/80 mt-1.5 max-w-2xl">
              A single contract from our <span className="text-gold font-semibold">self-performing management with AI-assist AI Saint</span> replaces <span className="text-gold font-semibold">~$2,400</span> in legal fees.
              AI bid comparison catches non-responsive quotes that typically cost boards <span className="text-gold font-semibold">10–25%</span> in change orders and scope creep.
              One project · one contract · your subscription is covered.
            </p>
            {impact && (
              <div className="mt-3 flex items-center gap-2 text-xs text-foreground/70" data-testid="roi-live-count">
                <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
                <span>
                  <span className="text-gold font-semibold">{impact.work_orders_closed_this_month}</span> work order{impact.work_orders_closed_this_month === 1 ? "" : "s"} closed this month ·{" "}
                  <span className="text-gold font-semibold">{impact.ai_contracts_generated}</span> AI contract{impact.ai_contracts_generated === 1 ? "" : "s"} on file ·{" "}
                  <span className="text-gold font-semibold">${(impact.dollars_managed / 1000).toFixed(1)}k</span> under AI management
                </span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 md:grid-cols-1 gap-2 md:min-w-[220px]">
            <RoiStat label="Legal drafting saved" value="$2,400" />
            <RoiStat label="Bid analysis catches" value="10-25%" />
            <RoiStat label="Elite yearly" value="$2,990" hint="/yr" />
          </div>
        </div>
      </div>

      {/* Current plan card */}
      <div className="hoa-card royal-ring p-8" data-testid="current-plan-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-gold-soft mb-1">Current plan</div>
            <div className="text-3xl font-semibold text-gold">{sub.plan?.name}</div>
            <div className="text-lg text-muted-foreground mt-1">{sub.plan?.tagline}</div>
          </div>
          <div className="text-right">
            <div className="text-4xl font-semibold text-gold font-mono">${sub.pricing.total.toFixed(2)}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">{sub.pricing.cycle} · total</div>
            {sub.nonprofit_discount && sub.pricing.discount > 0 && (
              <div className="text-xs text-gold mt-1">-${sub.pricing.discount.toFixed(2)} nonprofit discount</div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 pt-6 border-t border-border">
          <div><div className="text-xs text-muted-foreground uppercase">Base</div><div className="font-mono text-gold">${sub.pricing.base.toFixed(2)}</div></div>
          <div><div className="text-xs text-muted-foreground uppercase">Add-ons</div><div className="font-mono text-gold">${sub.pricing.addons.toFixed(2)}</div></div>
          <div><div className="text-xs text-muted-foreground uppercase">Nonprofit</div><div className="font-mono text-gold">{sub.nonprofit_discount ? `-${sub.nonprofit_discount_percent}%` : "—"}</div></div>
          <div><div className="text-xs text-muted-foreground uppercase">Cycle</div><div className="font-mono text-gold capitalize">{sub.pricing.cycle}</div></div>
        </div>
      </div>

      {/* Billing cycle */}
      {isBoard && (
        <div className="hoa-card p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="no-gold text-sm font-semibold text-foreground">Billing cycle</div>
              <p className="text-xs text-muted-foreground mt-1">Save two months when you switch to yearly. Nonprofit discount only applies on yearly.</p>
            </div>
            <div className="inline-flex items-center gap-1 bg-accent/50 border border-border rounded-full p-1" data-testid="billing-cycle-toggle">
              {["monthly", "yearly"].map((c) => (
                <button
                  key={c}
                  onClick={() => changePlan(sub.plan.id, c)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${cycle === c ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-gold"}`}
                  data-testid={`cycle-${c}`}
                >
                  {c === "monthly" ? "Monthly" : "Yearly"}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Plans grid */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Change plan</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {meta.plans.map((p) => {
            const Icon = PLAN_ICONS[p.id] || Zap;
            const current = sub.plan?.id === p.id;
            return (
              <div key={p.id} className={`hoa-card p-6 flex flex-col ${current ? "border-gold royal-ring" : ""} ${p.highlighted ? "glow-purple" : ""}`} data-testid={`plan-card-${p.id}`}>
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-primary/25 border border-gold flex items-center justify-center">
                    <Icon className="w-4.5 h-4.5 text-gold" />
                  </div>
                  <div className="no-gold text-lg font-semibold">{p.name}</div>
                </div>
                <div className="mb-4"><span className="text-2xl font-semibold text-gold font-mono">${price(p)}</span><span className="text-xs text-muted-foreground ml-1">{p.monthly === 0 ? "forever" : suffix}</span></div>
                <ul className="space-y-1.5 mb-5 flex-1">
                  {p.features.slice(0, 4).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-foreground/85">
                      <Check className="w-3.5 h-3.5 text-gold flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {current ? (
                  <div className="inline-flex items-center justify-center gap-1 rounded-full py-2 text-sm font-semibold bg-gold text-secondary-foreground" data-testid={`plan-current-${p.id}`}>
                    <Check className="w-3.5 h-3.5" /> Current
                  </div>
                ) : isBoard ? (
                  <div className="flex flex-col gap-2">
                    <Button
                      className="rounded-full h-10 bg-gold text-secondary-foreground hover:brightness-110"
                      onClick={() => checkout(p.id)}
                      disabled={checkingOut === p.id}
                      data-testid={`checkout-plan-${p.id}`}
                    >
                      <CreditCard className="w-4 h-4 mr-1.5" />
                      {checkingOut === p.id ? "Redirecting…" : `Start 14-day free trial · $${price(p)}${suffix}`}
                    </Button>
                    <Button variant="outline" className="rounded-full h-10" onClick={() => changePlan(p.id, cycle)} data-testid={`select-plan-${p.id}`}>
                      Just switch (skip payment)
                    </Button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {/* Add-ons */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Add-ons</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {meta.addons.map((a) => {
            const Icon = ADDON_ICONS[a.id] || Zap;
            const included = isIncluded(a.id);
            const st = addonStatus(a.id);
            const isTrial = st?.status === "trial";
            const isActive = st?.status === "active";
            return (
              <div key={a.id} className="hoa-card p-6 flex flex-col" data-testid={`addon-card-${a.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/25 border border-gold flex items-center justify-center">
                    <Icon className="w-5 h-5 text-gold" strokeWidth={2.2} />
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-semibold text-gold font-mono">${a.price}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">/mo</div>
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-gold-soft mb-1">{a.category}</div>
                <div className="no-gold text-base font-semibold mb-1">{a.name}</div>
                <p className="text-lg text-foreground/75 mb-4 leading-relaxed">{a.tagline}</p>

                <div className="mt-auto space-y-2">
                  {included && (
                    <div className="text-center rounded-full py-2 text-xs font-semibold bg-gold text-secondary-foreground" data-testid={`addon-included-${a.id}`}>
                      <Check className="w-3 h-3 inline mr-1" /> Included in {sub.plan.name}
                    </div>
                  )}
                  {!included && isTrial && (
                    <>
                      <div className="text-center text-xs text-gold" data-testid={`addon-trial-${a.id}`}>
                        Trial ends {new Date(st.expires_at).toLocaleDateString()}
                      </div>
                      {isBoard && (
                        <div className="flex gap-2">
                          <Button size="sm" className="rounded-full flex-1" onClick={() => activate(a.id)} data-testid={`addon-activate-${a.id}`}>Activate</Button>
                          <Button size="sm" variant="outline" onClick={() => remove(a.id)} data-testid={`addon-remove-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      )}
                    </>
                  )}
                  {!included && isActive && (
                    <>
                      <div className="text-center rounded-full py-2 text-xs font-semibold bg-primary text-primary-foreground" data-testid={`addon-active-${a.id}`}>
                        <Check className="w-3 h-3 inline mr-1" /> Active
                      </div>
                      {isBoard && (
                        <Button size="sm" variant="outline" className="w-full" onClick={() => remove(a.id)} data-testid={`addon-cancel-${a.id}`}>Cancel</Button>
                      )}
                    </>
                  )}
                  {!included && !st && isBoard && (
                    <div className="flex flex-col gap-2">
                      <Button size="sm" className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" onClick={() => startTrial(a.id)} data-testid={`addon-trial-btn-${a.id}`}>
                        <Sparkles className="w-3.5 h-3.5 mr-1" /> Start {a.trial_days}-day free trial
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => activate(a.id)} data-testid={`addon-activate-btn-${a.id}`}>
                        Activate now · ${a.price}/mo
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Nonprofit discount */}
      {isBoard && (
        <div className="hoa-card p-6 flex flex-col md:flex-row md:items-center gap-4" data-testid="nonprofit-card">
          <div className="w-12 h-12 rounded-xl bg-gold text-secondary-foreground flex items-center justify-center flex-shrink-0">
            <Building2 className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="no-gold text-base font-semibold">Nonprofit discount</div>
            <p className="text-xs text-muted-foreground mt-1">
              {sub.nonprofit_discount
                ? "Applied — 20% off any yearly subscription."
                : "If your HOA is incorporated as a nonprofit, save 20% on yearly billing. Verify your EIN below."}
            </p>
          </div>
          <Dialog open={npOpen} onOpenChange={setNpOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full" data-testid="nonprofit-verify-btn">
                {sub.nonprofit_discount ? "Update" : "Verify EIN"}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nonprofit verification</DialogTitle></DialogHeader>
              <form onSubmit={claimNonprofit} className="space-y-4" data-testid="nonprofit-form">
                <div>
                  <Label>Organization name</Label>
                  <Input required value={npForm.org_name} onChange={(e) => setNpForm({ ...npForm, org_name: e.target.value })} className="mt-1.5 h-11" data-testid="np-org" />
                </div>
                <div>
                  <Label>EIN</Label>
                  <Input required value={npForm.ein} onChange={(e) => setNpForm({ ...npForm, ein: e.target.value })} className="mt-1.5 h-11" placeholder="XX-XXXXXXX" data-testid="np-ein" />
                </div>
                <DialogFooter>
                  <Button type="submit" className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="np-submit">Apply discount</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}
